import presets from '../lib/cataloguePresets.json';
import {characterSlug, characterName} from '../lib/characters';
import {leonLayerScene} from '../components/configurator/leonLayers';
import {pralinLayerScene} from '../components/configurator/pralinLayers';
import {remainingModelScene} from '../components/configurator/remainingModelLayers';
import {sylvettePhoto} from '../components/configurator/sylvettePhotos';
import {configuratorImage} from './configuratorImages';
import delivery from './catalogueImageSources.json';
import packshots from './cataloguePackshots.json';

// Only approved, active configurator sources. No old cutouts, recolouring,
// invented stock photographs or destructive crop is introduced here.
// Photo « -verso » : le dos en Liberty de la pièce. Les sœurs d'un même modèle
// partagent la même face ; c'est leur dos qui les distingue.
const VERSO = /-verso\.webp(?:[?#]|$)/;

const MEDIA_TEXT = {
  fr: {front:'Devant', back:'Dos', view:n => `Vue ${n}`, main:'vue principale', whole:'corps et ruban entiers', fallback:'Marque-page'},
  en: {front:'Front', back:'Back', view:n => `View ${n}`, main:'main view', whole:'whole body and ribbon', fallback:'Bookmark'},
};

export function productMedia(product, {transparent = false, locale = 'fr'} = {}) {
  const text = MEDIA_TEXT[locale] || MEDIA_TEXT.fr;
  const slug = characterSlug(product?.slug);
  const preset = presets[slug];
  const name = characterName(slug, product?.name || text.fallback, locale);
  // Pièce du catalogue sans photo : fond teinté en attendant la photo de Margot
  // (le rendu générique du personnage n'aurait pas les couleurs de cette pièce).
  if (product?.character && product.has_photos === false) return [{
    id:'placeholder', label:text.front, alt:name, placeholder:product.placeholder_color || '#F2DDD4',
  }];
  // Chaque pièce montre ses propres photos dès qu'elles existent ; sinon le rendu du personnage.
  if (!preset || product?.has_photos) return (product?.images || []).map((src, i) => {
    const label = i === 0 ? text.front : VERSO.test(src) ? text.back : text.view(i + 1);
    return {id:`photo-${i}`, label, alt:`${name}, ${i === 0 ? text.main : label.toLowerCase()}`, src};
  });
  const options = Object.fromEntries(Object.entries(preset).map(([key, option_id]) => [
    key === 'ribbon' ? 'ribbonFabric' : key, {option_id},
  ]));
  return ['front', 'back'].map(view => {
    const label = view === 'front' ? text.front : text.back;
    const media = {id:view, label, alt:`${name}, ${label.toLowerCase()}, ${text.whole}`};
    if (!transparent && packshots[`${slug}:${view}`]) return {...media,...packshots[`${slug}:${view}`]};
    if (slug === 'sylvette-la-crevette') return {...media,
      src:sylvettePhoto(view === 'front' ? options.front : options.back, options.ribbonFabric, view).src};
    const props = {...options, slug, view};
    const scene = slug === 'leon-le-citron' ? leonLayerScene(props)
      : slug === 'pralin-le-cornet' ? pralinLayerScene(props) : remainingModelScene(props);
    return {...media, scene};
  });
}

/** Dos photographié de la pièce, s'il existe (montré au survol et en vignette sur les cartes). */
export function productVerso(product, {locale = 'fr'} = {}) {
  return productMedia(product, {transparent: true, locale}).find(item => VERSO.test(item.src || '')) || null;
}

export function catalogueImage(src, size = 640) {
  const record = delivery[src];
  if (!record || size === 'original') return configuratorImage(src);
  return record.variants.find(v => v.width >= size)?.src || configuratorImage(src);
}

export function catalogueImageProps(src, sizes, original = false) {
  const record = delivery[src];
  const base = {src:original ? configuratorImage(src) : catalogueImage(src)};
  if (!record || original) return base;
  return {...base, srcSet:[...record.variants.map(v => `${v.src} ${v.width}w`),
    `${configuratorImage(src)} ${record.width}w`].join(', '), sizes,
    width:record.width, height:record.height};
}

export function catalogueScene(scene, size) {
  if (!scene) return null;
  const result = {...scene};
  for (const key of ['body','ribbon','bodyMask','junctionMask','junctionOverlay']) {
    if (scene[key]) result[key] = catalogueImage(scene[key], size);
  }
  if (scene.bodyPanels) result.bodyPanels = scene.bodyPanels.map(p => ({...p, src:catalogueImage(p.src, size)}));
  return result;
}
